import { Modal } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { CiSearch } from 'react-icons/ci'
import classNames from 'classnames'
import DotsLoading from 'components/loadings/dotsLoading'
import AcceptIcon from 'assets/images/loans/accepted.svg'
import { getUsers } from 'services/withDraw'

export const MAX_GUARANTORS = 2

export interface IGuarantorOption {
    poolship_id: number
    name: string
}

interface IGuarantorPickerProps {
    show: boolean
    onHide: () => void
    moneypoolId?: string | number
    /** already-picked guarantors; the modal opens with these ticked */
    value: Array<IGuarantorOption>
    onChange: (guarantors: Array<IGuarantorOption>) => void
    /**
     * The borrower — never offered as an option, since nobody can guarantee
     * their own loan (the backend rejects it too).
     */
    excludePoolshipId?: number
    /**
     * The borrower's ضامن عضویت, pinned to the top of the list. Funds record
     * this when a member joins and it is overwhelmingly the same person who
     * backs their loan, so surfacing it saves scrolling a 900-member list.
     */
    suggested?: IGuarantorOption | null
    title?: string
}

export default function GuarantorPickerModal({
    show,
    onHide,
    moneypoolId,
    value,
    onChange,
    excludePoolshipId,
    suggested,
    title,
}: IGuarantorPickerProps) {
    const [selected, setSelected] = useState<Array<IGuarantorOption>>(value)
    const [searchInput, setSearchInput] = useState<string>('')
    const [searchQuery, setSearchQuery] = useState<string>(' ')

    const { data, isLoading } = useQuery(
        ['guarantor-picker-users', moneypoolId, searchQuery],
        getUsers,
        { enabled: show && !!moneypoolId }
    )

    // Re-sync whenever the modal is re-opened: the parent is the source of
    // truth, and a cancelled edit must not leak into the next open.
    useEffect(() => {
        if (show) {
            setSelected(value)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show])

    useEffect(() => {
        if (searchInput === '') {
            setSearchQuery('')
        }
    }, [searchInput])

    const isSelected = (poolshipId: number) =>
        selected.some((item) => item.poolship_id === poolshipId)

    const toggle = (option: IGuarantorOption) => {
        if (isSelected(option.poolship_id)) {
            setSelected(
                selected.filter(
                    (item) => item.poolship_id !== option.poolship_id
                )
            )
            return
        }
        if (selected.length >= MAX_GUARANTORS) {
            // Drop the oldest pick rather than silently ignoring the tap —
            // with a 2-slot limit, "replace" is what people actually mean.
            setSelected([...selected.slice(1), option])
            return
        }
        setSelected([...selected, option])
    }

    const options: Array<IGuarantorOption> = []
    if (suggested && suggested.poolship_id !== excludePoolshipId) {
        options.push(suggested)
    }
    data?.results?.forEach((item: any) => {
        if (!item.is_active) return
        if (item.id === excludePoolshipId) return
        if (options.some((option) => option.poolship_id === item.id)) return
        options.push({
            poolship_id: item.id,
            name: `${item.member?.first_name ?? ''} ${
                item.member?.last_name ?? ''
            }`.trim(),
        })
    })

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <div className="py-3 px-2">
                <p className="text-center text-sm">
                    {title ?? 'ضامن وام را انتخاب کنید'}
                </p>
                <p className="text-center text-xs text-gray-4 mt-1">
                    حداکثر {MAX_GUARANTORS} ضامن — انتخاب اختیاری است
                </p>
                <hr className="my-2" />

                <div className="flex h-[25px]">
                    <input
                        type="text"
                        className="w-full outline-0 border border-green-1 rounded-full px-2 py-1 ml-3"
                        onChange={(e) => setSearchInput(e.target.value)}
                        value={searchInput}
                        placeholder="جستجو..."
                    />
                    <CiSearch
                        className="text-green-1 w-8 h-full cursor-pointer"
                        onClick={() => setSearchQuery(searchInput)}
                    />
                </div>

                <div className="h-72 overflow-y-scroll">
                    {isLoading ? (
                        <DotsLoading />
                    ) : options.length === 0 ? (
                        <p className="text-center text-sm text-gray-4 mt-8">
                            عضوی برای انتخاب پیدا نشد
                        </p>
                    ) : (
                        options.map((option) => (
                            <div
                                key={option.poolship_id}
                                className={classNames(
                                    'my-3 flex justify-between items-center px-2 cursor-pointer py-1',
                                    isSelected(option.poolship_id) &&
                                        'rounded-full border-1 border-green-1'
                                )}
                                onClick={() => toggle(option)}
                            >
                                <p
                                    className={classNames(
                                        'text-sm',
                                        isSelected(option.poolship_id) &&
                                            'text-green-1'
                                    )}
                                >
                                    {option.name}
                                    {suggested?.poolship_id ===
                                        option.poolship_id && (
                                        <span className="text-[10px] text-blue-1 mr-2 border border-blue-1 rounded-full px-2 py-[1px]">
                                            ضامن عضویت
                                        </span>
                                    )}
                                </p>
                                {isSelected(option.poolship_id) && (
                                    <img src={AcceptIcon} alt="selected" />
                                )}
                            </div>
                        ))
                    )}
                </div>

                <hr className="my-2" />
                <div className="flex justify-between px-4">
                    <p
                        className="text-gray-4 cursor-pointer"
                        onClick={() => {
                            setSelected([])
                            onChange([])
                            onHide()
                        }}
                    >
                        بدون ضامن
                    </p>
                    <p
                        className="text-green-1 cursor-pointer"
                        onClick={() => {
                            onChange(selected)
                            onHide()
                        }}
                    >
                        تایید
                    </p>
                </div>
            </div>
        </Modal>
    )
}
